import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import type { Translate } from "@similarweb/i18n-provider";
import { useTranslate } from "@similarweb/i18n-provider";
import type { ProWebsiteRankTrackerModelGscSiteResponse } from "@similarweb/pro-api-client";

import { apiClient } from "app/modules/api-client/apiClient";
import {
    ALL_CAMPAIGNS_QUERY_PREFIX,
    RankTrackerQueryType,
} from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { useRankTrackerQuery } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks/useRankTrackerQuery";
import { createCampaignQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";

import { handleGscThrowOnError } from "../gscProblemErrors";
import type { GscSite } from "../types";
import { extractGscDomain, formatGscDisplayText } from "../utils";

export type GscPropertiesQueryParams = {
    connectionId: number;
};

type QueryOptions = Omit<UseQueryOptions<GscSite[]>, "queryKey" | "queryFn">;

const fetchGscProperties = async (
    params: GscPropertiesQueryParams,
): Promise<ProWebsiteRankTrackerModelGscSiteResponse[]> => {
    return apiClient.RankTracker.rtGetGscSites(params.connectionId);
};

const processGscSites = (sites: ProWebsiteRankTrackerModelGscSiteResponse[], translate: Translate): GscSite[] => {
    return sites.map((site) => ({
        ...site,
        domain: extractGscDomain(site.site),
        displayText: formatGscDisplayText(site.site, translate),
    }));
};

/** Fetches GSC properties (websites) for a given connection. */
export const useGscPropertiesQuery = (params: GscPropertiesQueryParams, options?: QueryOptions) => {
    const { translate } = useTranslate();

    const queryKey: QueryKey = createCampaignQueryKey(ALL_CAMPAIGNS_QUERY_PREFIX, RankTrackerQueryType.GscProperties, [
        params.connectionId,
    ]);

    return useRankTrackerQuery({
        queryKey,
        queryFn: () => fetchGscProperties(params).then((sites) => processGscSites(sites, translate)),
        throwOnError: handleGscThrowOnError("Error fetching GSC properties"),
        ...options,
    });
};
